/**
 * RegionStrip — a horizontal row of clickable pills for regional analyses
 * (EU today; LATAM, ASEAN, etc. when added). Lives under the legend in the
 * header so the user never has to go searching for "EU".
 */
import { theme } from '@/analyses/_shared/theme';
import { REGIONS } from '@/data/regions';
import { TIER_CONFIG } from '@/data/tiers';

export type RegionStripProps = {
  onSelectRegion: (regionName: string) => void;
};

export const RegionStrip = ({ onSelectRegion }: RegionStripProps) => {
  const analyzedRegions = Object.values(REGIONS).filter((r) => r.analysisKey);
  if (analyzedRegions.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          color: theme.MUTED,
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
        }}
      >
        Regional analyses
      </span>
      {analyzedRegions.map((r) => {
        const cfg = TIER_CONFIG[r.tier ?? 'not-analyzed'];
        return (
          <button
            key={r.id}
            onClick={() => onSelectRegion(r.name)}
            title={r.summary ?? 'Open regional analysis'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: theme.CARD,
              border: `1px solid ${theme.BORDER}`,
              color: theme.WHITE,
              borderRadius: 16,
              padding: '4px 10px 4px 8px',
              fontSize: 11.5,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.BLUE;
              e.currentTarget.style.background = theme.DARK2;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.BORDER;
              e.currentTarget.style.background = theme.CARD;
            }}
          >
            <span
              style={{
                background: theme.BLUE,
                color: theme.WHITE,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: '1px 5px',
                borderRadius: 3,
                textTransform: 'uppercase',
              }}
            >
              Region
            </span>
            <span>{r.name}</span>
            <span
              style={{
                color: theme.MUTED,
                fontSize: 10.5,
              }}
            >
              {r.memberCountries.length}
            </span>
            {r.tier && (
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: cfg.color,
                  marginLeft: 2,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
